import React, { useState } from 'react';
import { 
  DocumentArrowUpIcon, 
  XMarkIcon, 
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const BulkMessageFileUpload = ({ onFileChange, uploadedFile, onRemove, uploading }) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    setError('');
    
    // Validate file type
    const allowedTypes = [
      'text/csv',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setError('Only CSV, TXT, and Excel files are allowed');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size should be less than 10MB');
      return;
    }
    
    // Upload file to backend
    const uploadFile = async () => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'bulk-recipients');
      
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/upload/bulk-file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('snwolley_auth_token')}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('Failed to upload file');
        }
        
        const result = await response.json();
        
        const fileData = {
          name: file.name,
          type: file.type,
          size: file.size,
          url: result.url,
          path: result.path,
          lastModified: file.lastModified
        };
        
        onFileChange(fileData);
      } catch (error) {
        setError('Failed to upload file. Please try again.');
        console.error('Upload error:', error);
      }
    };
    
    uploadFile();
  };

  const removeFile = () => {
    onRemove();
    setError('');
  };

  const getFileIcon = () => {
    if (!uploadedFile) return DocumentArrowUpIcon;
    
    if (uploadedFile.type === 'text/csv') return DocumentTextIcon;
    if (uploadedFile.type === 'text/plain') return DocumentTextIcon;
    if (uploadedFile.type.includes('excel')) return DocumentTextIcon;
    
    return DocumentTextIcon;
  };

  const getFileTypeLabel = () => {
    if (!uploadedFile) return '';
    
    if (uploadedFile.type === 'text/csv') return 'CSV';
    if (uploadedFile.type === 'text/plain') return 'TXT';
    if (uploadedFile.type === 'application/vnd.ms-excel') return 'Excel';
    if (uploadedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'Excel';
    
    return uploadedFile.type.split('/')[1].toUpperCase();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Recipient List <span className="text-red-500">*</span>
        </label>
        {uploadedFile && (
          <button
            type="button"
            onClick={removeFile}
            disabled={uploading}
            className="text-red-500 hover:text-red-700 text-sm font-medium flex items-center disabled:opacity-50"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            Remove File
          </button>
        )}
      </div>

      {!uploadedFile ? (
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors
            ${dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${uploading ? 'opacity-50 pointer-events-none' : ''}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            disabled={uploading}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            accept=".csv,.txt,.xls,.xlsx"
            onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
          />
          <getFileIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <label className="cursor-pointer">
              <span className="mt-2 block text-sm font-medium text-gray-900">
                {uploading ? 'Processing...' : 'Click to upload or drag and drop'}
              </span>
              <span className="mt-1 block text-xs text-gray-500">
                CSV, TXT, or Excel files up to 10MB
              </span>
            </label>
          </div>
        </div>
      ) : (
        <div className="border rounded-lg p-4 bg-green-50 border-green-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-6 w-6 text-green-400" />
            </div>
            <div className="ml-3 flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-green-800">
                    {uploadedFile.name}
                  </h4>
                  <p className="text-xs text-green-600 mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                      {getFileTypeLabel()}
                    </span>
                    {formatFileSize(uploadedFile.size)}
                  </p>
                </div>
                <div className="text-xs text-green-600">
                  {uploading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    'Ready to upload'
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <DocumentTextIcon className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">File Format Guidelines</h4>
            <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
              <li><strong>CSV/Excel:</strong> First column should contain phone numbers (headers are automatically skipped)</li>
              <li><strong>TXT:</strong> One phone number per line (format: 233554694531)</li>
              <li><strong>Format:</strong> Use international format without + or spaces</li>
            </ul>
            <div className="mt-3">
              <a
                href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/uploads/bulk_template.xlsx`}
                download="sample_bulk_template.csv"
                className="inline-flex items-center text-sm text-red-600 hover:text-red-800 font-medium"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Sample Bulk Template
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkMessageFileUpload;